import { useEffect, useState } from "react";
import { MapPin, Phone, Clock, Mail, Instagram, Navigation } from "lucide-react";
import { INFO } from "@/lib/data";
import { Reveal, MaskedLines } from "./motion";
import { getConsent, setConsent, CONSENT_EVENT } from "@/lib/consent";

export default function Contacts() {
  const [consent, setConsentState] = useState(getConsent());
  useEffect(() => {
    const h = (e) => setConsentState(e.detail || getConsent());
    window.addEventListener(CONSENT_EVENT, h);
    return () => window.removeEventListener(CONSENT_EVENT, h);
  }, []);
  const mapAllowed = consent === "all";
  return (
    <section id="contatti" className="relative bg-bone py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
          {/* Info */}
          <div className="flex flex-col">
            <span className="text-xs tracking-[0.25em] uppercase font-sans font-semibold text-terracotta">
              Dove siamo
            </span>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-charcoal">
              <MaskedLines lines={["Ti aspettiamo"]} />
              <MaskedLines lines={["a Cavi di Lavagna."]} lineClassName="italic text-terracotta" delay={0.1} />
            </h2>

            <div className="mt-10 flex flex-col divide-y divide-charcoal/10">
              {[
                { icon: MapPin, label: "Indirizzo", value: `${INFO.street}, ${INFO.cap} ${INFO.city} (${INFO.province})` },
                { icon: Phone, label: "Telefono", value: INFO.phoneDisplay, href: `tel:${INFO.phoneTel}` },
                { icon: Clock, label: "Orari", value: INFO.hours },
                { icon: Mail, label: "Email", value: INFO.email, href: `mailto:${INFO.email}` },
                { icon: Instagram, label: "Instagram", value: "@bar.gabri08", href: INFO.instagram },
              ].map((row) => {
                const Icon = row.icon;
                const content = (
                  <div className="flex items-start gap-4 py-5 group">
                    <Icon size={20} className="text-terracotta mt-0.5 shrink-0" />
                    <div>
                      <div className="text-xs uppercase tracking-wide text-pebble font-sans">{row.label}</div>
                      <div className="font-serif text-xl text-charcoal group-hover:text-terracotta transition-colors duration-300">
                        {row.value}
                      </div>
                    </div>
                  </div>
                );
                return row.href ? (
                  <Reveal key={row.label}>
                    <a href={row.href} target={row.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block">
                      {content}
                    </a>
                  </Reveal>
                ) : (
                  <Reveal key={row.label}>{content}</Reveal>
                );
              })}
            </div>

            <a
              href={INFO.mapLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-open-btn"
              className="mt-8 self-start flex items-center gap-2 bg-charcoal hover:bg-terracotta text-bone px-7 py-3.5 rounded-sm font-semibold tracking-wide transition-colors duration-300"
            >
              <Navigation size={17} /> Apri la mappa
            </a>
          </div>

          {/* Map — loaded only after cookie consent (Google may set cookies) */}
          <Reveal delay={0.1} className="min-h-[380px]">
            <div className="h-full w-full rounded-sm overflow-hidden border border-charcoal/10 shadow-lg">
              {mapAllowed ? (
                <iframe
                  title="Mappa Bar Ristorante Gabri Lavagna"
                  data-testid="google-map-iframe"
                  src={INFO.mapEmbed}
                  className="w-full h-full min-h-[380px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              ) : (
                <div
                  data-testid="map-consent-placeholder"
                  className="w-full h-full min-h-[380px] bg-sand flex flex-col items-center justify-center text-center px-6 py-10 gap-4"
                >
                  <MapPin size={32} className="text-terracotta" />
                  <p className="font-sans text-sm text-pebble max-w-xs leading-relaxed">
                    Per mostrare la mappa Google è necessario il tuo consenso ai
                    cookie di terze parti.
                  </p>
                  <button
                    data-testid="map-enable-btn"
                    onClick={() => setConsent("all")}
                    className="bg-charcoal hover:bg-terracotta text-bone px-6 py-3 rounded-sm text-sm font-semibold tracking-wide transition-colors duration-300"
                  >
                    Attiva la mappa
                  </button>
                  <a
                    href={INFO.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-pebble underline underline-offset-2 hover:text-terracotta"
                  >
                    oppure apri Google Maps in una nuova scheda
                  </a>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
